import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

type AdsDuplicationSettings = {
  environment: "development" | "production" | null;
  editable: boolean;
  brands: {
    id: string;
    name: string;
    status: string;
    enabled: boolean;
  }[];
};

type BrandUpdate = {
  id: string;
  name: string;
  status: string;
  enabled: boolean;
};

const settingsKey = ["/api/admin/ads-manager-duplication"];

function errorMessage(error: unknown) {
  const raw = error instanceof Error ? error.message : "Request failed";
  try {
    const parsed = JSON.parse(raw.replace(/^\d+:\s*/, ""));
    return parsed.error || parsed.message || raw;
  } catch {
    return raw;
  }
}

export function AdsManagerDuplicationToggle() {
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState("");
  const [saveStatus, setSaveStatus] = useState("");

  const query = useQuery<AdsDuplicationSettings>({
    queryKey: settingsKey,
    retry: false,
    queryFn: async () => (await apiRequest("GET", settingsKey[0])).json(),
  });

  const updateBrand = useMutation({
    mutationFn: async ({ brandId, enabled }: { brandId: string; enabled: boolean }) =>
      (await apiRequest(
        "PATCH",
        `${settingsKey[0]}/brands/${encodeURIComponent(brandId)}`,
        { enabled },
      )).json() as Promise<BrandUpdate>,
    onMutate: () => {
      setSaveError("");
      setSaveStatus("");
    },
    onSuccess: async (brand) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: settingsKey }),
        queryClient.invalidateQueries({ queryKey: ["/api/admin/brands"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/brands"] }),
        queryClient.invalidateQueries({
          queryKey: ["/api/brands", brand.id, "ads-manager", "duplicate-ads", "capabilities"],
        }),
      ]);
      setSaveStatus(`${brand.name} proof-engine development access ${brand.enabled ? "enabled" : "disabled"}.`);
    },
    onError: (error) => {
      setSaveError(errorMessage(error));
    },
  });

  const busy = updateBrand.isPending;

  return (
    <Card data-testid="ads-manager-duplication-toggle">
      <CardHeader>
        <CardTitle>Proof-preserving duplication testing</CardTitle>
        <CardDescription>
          Editable Duplicate drafts are available automatically in development and production
          when both Ads Manager and Ad Launcher are enabled for an active brand.
          These switches only control the separate proof engine's development grant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border border-border/70 bg-muted/40 px-4 py-3">
          <p className="text-sm font-medium">Development testing only</p>
          <p className="mt-1 text-sm text-muted-foreground">
            This control does not govern the Duplicate button or editable imports.
            Editable imports do not preserve engagement and never launch automatically.
            Enabling a proof-engine grant does not enable production or approve a live Meta sandbox pilot.
          </p>
        </div>

        {query.isPending ? (
          <div className="space-y-2" aria-label="Loading Ads Manager duplication settings">
            <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            <div className="h-14 animate-pulse rounded-md bg-muted/70" />
            <div className="h-14 animate-pulse rounded-md bg-muted/70" />
          </div>
        ) : query.isError ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4">
            <p className="text-sm text-destructive">
              Could not load Ads Manager duplication settings: {errorMessage(query.error)}
            </p>
            <Button variant="outline" onClick={() => query.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
              <div>
                <p className="text-sm font-medium">Proof-engine development access</p>
                <p className="text-xs text-muted-foreground">
                  {query.data.environment
                    ? `Current environment: ${query.data.environment}`
                    : "Environment not reported"}
                </p>
              </div>
              {!query.data.editable && (
                <span className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
                  Read only
                </span>
              )}
            </div>

            {query.data.brands.length ? (
              <div className="divide-y rounded-md border">
                {query.data.brands.map((brand) => (
                  <div
                    key={brand.id}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{brand.name}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {brand.status}
                      </p>
                    </div>
                    <Switch
                      aria-label={`Proof-engine development access for ${brand.name}`}
                      checked={brand.enabled === true}
                      disabled={!query.data.editable || busy}
                      onCheckedChange={(enabled) =>
                        updateBrand.mutate({ brandId: brand.id, enabled })
                      }
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed px-4 py-7 text-center">
                <p className="text-sm font-medium">No brands available</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Brand development access will appear here when brands are available.
                </p>
              </div>
            )}
          </>
        )}

        {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}
        {saveStatus && <p role="status" className="text-sm text-muted-foreground">{saveStatus}</p>}
      </CardContent>
    </Card>
  );
}
